/**
 * Referral Dashboard
 *
 * Comprehensive admin dashboard for monitoring guide referrals and HubSpot sync.
 * Displays referral statistics, top referrers, earnings, and sync status.
 *
 * Features:
 * - Real-time referral statistics
 * - Top referrers leaderboard
 * - Revenue share and payout tracking
 * - HubSpot sync status and manual trigger
 * - Recent referrals list
 *
 * Epic: MVP 1.13 - HubSpot & Guide Referral Management
 *
 * TODO: Add authentication and authorization when permissions system is implemented
 * TODO: Restrict access to admin users only
 * Current Status: Protected by adminDashboardLoader - requires super_admin or staff role
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  DollarSign,
  TrendingUp,
  Award,
  RefreshCw,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import DashboardLayout from '../layout/DashboardLayout';
import apiRequest from '../../services/api';
import StatusBadge from '../common/StatusBadge';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';
import TabGroup from '../common/TabGroup';
import EmptyState from '../common/EmptyState';

// API client functions

// /overview returns { summary, recentReferrals, topReferrers }. summary is
// camelCase; the recentReferrals/topReferrers rows are snake_case from raw SQL,
// so they're mapped to the field names the table components expect.
const fetchOverview = async () => {
  return apiRequest('/api/v1/referrals/overview', { method: 'GET' });
};

const fetchHubSpotSyncStatus = async () => {
  return apiRequest('/api/v1/referrals/sync/status', { method: 'GET' });
};

const mapStats = (summary = {}) => ({
  totalReferrals: summary.totalReferrals || 0,
  activeReferrals: summary.activatedCount || 0,
  pendingReferrals: summary.pendingCount || 0,
  totalPayoutsCents: summary.totalPaidCents || 0,
  pendingPayoutsCents: summary.totalPendingCents || 0
});

const mapRecentReferrals = (rows = []) => rows.map((r) => ({
  id: r.id,
  referredGuideName: r.referred_name,
  referredGuideEmail: r.referred_email,
  referrerGuideName: r.referrer_name,
  referrerGuideEmail: r.referrer_email,
  status: (r.referral_status || '').toLowerCase(),
  totalEarningsCents: r.total_payout_cents,
  createdAt: r.referred_at
}));

const mapTopReferrers = (rows = []) => rows.map((r) => ({
  guideId: r.referrer_guide_id,
  guideName: r.referrer_name,
  guideEmail: r.referrer_email,
  referralCount: r.total_referrals,
  totalEarningsCents: r.total_paid_cents
}));

const triggerHubSpotSync = async () => {
  const response = await apiRequest('/api/v1/referrals/sync', {
    method: 'POST'
  });
  return response;
};

export default function ReferralDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentReferrals, setRecentReferrals] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, referrals, top-referrers, sync

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overview, syncData] = await Promise.all([
        fetchOverview(),
        fetchHubSpotSyncStatus()
      ]);

      setStats(mapStats(overview.summary));
      setRecentReferrals(mapRecentReferrals(overview.recentReferrals));
      setTopReferrers(mapTopReferrers(overview.topReferrers));
      setSyncStatus(syncData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTrigger = async () => {
    setSyncing(true);
    setError(null);
    try {
      await triggerHubSpotSync();
      // Reload sync status after triggering
      const syncData = await fetchHubSpotSyncStatus();
      setSyncStatus(syncData);
    } catch (err) {
      setError(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout
        userRole="admin"
        activeNavId="referrals"
        onNavigate={handleNavigate}
        user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
      >
        <LoadingSpinner message="Loading referral data..." size="lg" />
      </DashboardLayout>
    );
  }

  if (error && !stats) {
    return (
      <DashboardLayout
        userRole="admin"
        activeNavId="referrals"
        onNavigate={handleNavigate}
        user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
      >
        <Alert variant="error" message={`Error Loading Dashboard: ${error}`} />
        <button
          onClick={loadDashboardData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="referrals"
      onNavigate={handleNavigate}
      user={{ name: 'Admin User', email: 'admin@outdoorguides.com', avatar: null }}
    >
        {/* Page Header */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            Referral Management
          </h1>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="warning" message={error} className="mb-6" />
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Total Referrals */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">Total Referrals</span>
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.totalReferrals || 0}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {stats?.activeReferrals || 0} active
            </div>
          </div>

          {/* Total Payouts */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">Total Payouts</span>
              <DollarSign className="h-5 w-5 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              ${((stats?.totalPayoutsCents || 0) / 100).toFixed(2)}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {stats?.completedPayouts || 0} paid
            </div>
          </div>

          {/* Revenue Share */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">Revenue Share</span>
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              ${((stats?.totalRevenueShareCents || 0) / 100).toFixed(2)}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              from {stats?.revenueShareBookings || 0} bookings
            </div>
          </div>

          {/* Avg Referrals per Guide */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm font-medium">Avg per Referrer</span>
              <Award className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.avgReferralsPerGuide?.toFixed(1) || '0.0'}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {stats?.uniqueReferrers || 0} active referrers
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <TabGroup
            tabs={[
              { id: 'overview', label: 'Overview' },
              { id: 'referrals', label: 'Recent Referrals' },
              { id: 'top-referrers', label: 'Top Referrers' },
              { id: 'sync', label: 'HubSpot Sync' }
            ]}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <ReferralOverview stats={stats} />
            )}

            {activeTab === 'referrals' && (
              <RecentReferrals referrals={recentReferrals} />
            )}

            {activeTab === 'top-referrers' && (
              <TopReferrers referrers={topReferrers} />
            )}

            {activeTab === 'sync' && (
              <HubSpotSync
                status={syncStatus}
                onSync={handleSyncTrigger}
                syncing={syncing}
              />
            )}
          </div>
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end">
          <button
            onClick={loadDashboardData}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </button>
        </div>
    </DashboardLayout>
  );
}

// Overview Tab Component
function ReferralOverview({ stats }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Referral Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pending Activation</span>
              <span className="font-semibold text-gray-900">{stats?.pendingReferrals || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Active</span>
              <span className="font-semibold text-green-600">{stats?.activeReferrals || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Completed</span>
              <span className="font-semibold text-blue-600">{stats?.completedReferrals || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payout Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Base Bonuses</span>
              <span className="font-semibold text-gray-900">
                ${((stats?.baseBonusesCents || 0) / 100).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Revenue Share</span>
              <span className="font-semibold text-gray-900">
                ${((stats?.totalRevenueShareCents || 0) / 100).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pending Payouts</span>
              <span className="font-semibold text-yellow-600">
                ${((stats?.pendingPayoutsCents || 0) / 100).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Recent Referrals Tab Component
function RecentReferrals({ referrals }) {
  if (!referrals || referrals.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No referrals found"
        description="There are no referrals to display yet."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Referred Guide
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Referrer
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Earnings
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {referrals.map((referral) => (
            <tr key={referral.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {referral.referredGuideName || referral.referredGuideEmail}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {referral.referrerGuideName || referral.referrerGuideEmail}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <StatusBadge variant={
                  referral.status === 'pending' ? 'warning' :
                  referral.status === 'active' ? 'info' :
                  referral.status === 'completed' ? 'success' :
                  referral.status === 'expired' ? 'neutral' : 'neutral'
                }>
                  {referral.status ? referral.status.charAt(0).toUpperCase() + referral.status.slice(1) : 'Pending'}
                </StatusBadge>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                ${((referral.totalEarningsCents || 0) / 100).toFixed(2)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(referral.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Top Referrers Tab Component
function TopReferrers({ referrers }) {
  if (!referrers || referrers.length === 0) {
    return (
      <EmptyState
        icon={Award}
        title="No referrers found"
        description="There are no top referrers to display yet."
      />
    );
  }

  return (
    <div className="space-y-4">
      {referrers.map((referrer, index) => (
        <div
          key={referrer.guideId}
          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-4">
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg
              ${index === 0 ? 'bg-yellow-100 text-yellow-700' : ''}
              ${index === 1 ? 'bg-gray-100 text-gray-700' : ''}
              ${index === 2 ? 'bg-orange-100 text-orange-700' : ''}
              ${index > 2 ? 'bg-blue-100 text-blue-700' : ''}
            `}>
              {index + 1}
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {referrer.guideName || referrer.guideEmail}
              </div>
              <div className="text-sm text-gray-600">
                {referrer.referralCount} referral{referrer.referralCount !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-semibold text-gray-900">
              ${((referrer.totalEarningsCents || 0) / 100).toFixed(2)}
            </div>
            <div className="text-sm text-gray-600">earned</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// HubSpot Sync Tab Component
function HubSpotSync({ status, onSync, syncing }) {
  if (!status) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="HubSpot sync status unavailable"
        description="Unable to retrieve sync status at this time."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Sync Status Card */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Sync Status</h3>
          <button
            onClick={onSync}
            disabled={syncing}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span className="text-gray-600 text-sm">Last Sync</span>
            <div className="font-semibold text-gray-900 mt-1">
              {status.lastSyncAt
                ? new Date(status.lastSyncAt).toLocaleString()
                : 'Never'
              }
            </div>
          </div>
          <div>
            <span className="text-gray-600 text-sm">Next Scheduled</span>
            <div className="font-semibold text-gray-900 mt-1">
              {status.nextSyncAt
                ? new Date(status.nextSyncAt).toLocaleString()
                : 'Not scheduled'
              }
            </div>
          </div>
          <div>
            <span className="text-gray-600 text-sm">Sync Frequency</span>
            <div className="font-semibold text-gray-900 mt-1">
              {status.syncSchedule || 'Daily at 2:00 AM CT'}
            </div>
          </div>
        </div>
      </div>

      {/* Sync Stats */}
      {status.lastSyncStats && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Last Sync Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {status.lastSyncStats.synced || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Synced</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {status.lastSyncStats.created || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Created</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {status.lastSyncStats.updated || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Updated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {status.lastSyncStats.failed || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Failed</div>
            </div>
          </div>
        </div>
      )}

      {/* HubSpot Link */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ExternalLink className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-blue-900 font-medium">View in HubSpot</span>
          </div>
          <a
            href="https://app.hubspot.com/contacts"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Open HubSpot →
          </a>
        </div>
      </div>
    </div>
  );
}

